import { Component, OnInit, inject, ViewChild, TemplateRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageShell } from '../../shared/components/page-shell/page-shell';
import { AppPrimeTable, PrimeTableColumn } from '../../shared/components/prime-table/prime-table';
import { PatientsService } from '../../core/services/patient.service';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, switchMap, tap, startWith } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppDropdown } from '../../shared/components/dropdown/dropdown';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { PatientModal } from './patient-modal/patient-modal';
import { AppButton } from '../../shared/components/app-button/app-button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { toast } from 'ngx-sonner';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { PatientIdentifierPipe } from '../../shared/pipes/patient-identifier.pipe';
import { SignedSrcDirective } from '../../shared/directives/signed-src.directive';
import { LeadConversionService, LeadContact } from '../../core/services/lead-conversion.service';
import { PermissionService, MODULE } from '../../core/services/permission.service';

@Component({
  standalone: true,
  imports: [CommonModule, AppPrimeTable, TranslateModule, PageShell, IconFieldModule, InputIconModule, InputTextModule, FormsModule, PatientModal, RouterModule, AppDropdown, AppButton, ConfirmDialogModule, HasPermissionDirective, PatientIdentifierPipe, SignedSrcDirective],

  providers: [ConfirmationService],
  templateUrl: './patients.html'
})
export class PatientsPage implements OnInit, AfterViewInit {
  private patientsService = inject(PatientsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private confirmationService = inject(ConfirmationService);
  private leadConversion = inject(LeadConversionService);
  private permissionService = inject(PermissionService);
  private translate = inject(TranslateService);

  @ViewChild('photoTemplate') photoTemplate!: TemplateRef<any>;
  @ViewChild('statusTemplate') statusTemplate!: TemplateRef<any>;
  @ViewChild('cellTemplate') cellTemplate!: TemplateRef<any>;
  @ViewChild('createdByTemplate') createdByTemplate!: TemplateRef<any>;
  @ViewChild('updatedByTemplate') updatedByTemplate!: TemplateRef<any>;
  @ViewChild('actionsTemplate') actionsTemplate!: TemplateRef<any>;

  columns: PrimeTableColumn[] = [
    { field: 'photo', header: 'table_patient', width: '25%', filter: true },
    { field: 'bi_cc', header: 'table_document', filter: true },
    { field: 'status', header: 'table_status', filter: true },
    // The column shows the designated contact number, not the `cell` column -
    // field also keys the custom-template lookup, so it moves with the meaning.
    { field: 'primary_phone', header: 'patient_field_primary_contact', type: 'custom', filter: true },
    { field: 'email', header: 'table_email', filter: true },
    { field: 'created_by', header: 'table_created_by', type: 'custom', filter: false, styleClass: 'whitespace-nowrap' },
    { field: 'updated_by', header: 'table_updated_by', type: 'custom', filter: false, styleClass: 'whitespace-nowrap' },
    { field: 'actions', header: 'table_action', width: '100px', styleClass: 'text-center', type: 'action', filter: false }
  ];



  templates: { [key: string]: TemplateRef<any> } = {};

  searchTerm = '';
  search$ = new BehaviorSubject<string>('');
  page$ = new BehaviorSubject<number>(1);
  pageSize = 10;

  private refresh$ = new BehaviorSubject<void>(undefined);
  
  private currentData: any = null;
  private currentCount: number = 0;

  showModal = false;
  selectedPatientId?: number;

  loading$ = new BehaviorSubject<boolean>(true);
  // page$ is nested inside this switchMap (rather than combined via combineLatest)
  // so that a search/refresh change fully replaces the page subscription instead
  // of racing it — combining them directly fired two requests per search change:
  // one with the still-stale search text (while page reset to 1), then the real one.
  patientsData$ = combineLatest([
    this.search$.pipe(debounceTime(400), distinctUntilChanged()),
    this.refresh$
  ]).pipe(
    switchMap(([search]) => {
      this.page$.next(1);
      return this.page$.pipe(
        distinctUntilChanged(),
        switchMap((page) => {
          this.isManualRefresh = false;
          this.loading$.next(true);
          const offset = (page - 1) * this.pageSize;
          return this.patientsService.getPatients(search, this.pageSize, offset);
        }),
      );
    }),
    tap(() => {
      this.loading$.next(false);
    }),
    startWith({ data: [], count: 0 })
  );

  private isManualRefresh = false;

  /**
   * Conversion context, when this page was opened in a new tab from a lead
   * appointment's "Convert to patient" button (see appointment-detail-modal).
   * Both stay null for an ordinary "New patient".
   */
  patientSeed: LeadContact | null = null;
  linkAppointmentId?: number;

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      // The route guard only requires 'Pacientes' (view), so without this check
      // ?openAddModal=true hands the create form to anyone who can merely view
      // the list — bypassing the *hasPermission on the New patient button.
      // Pre-existing, but the Convert-to-patient flow makes this URL reachable.
      if (!this.permissionService.canCreate(MODULE.PACIENTES)) {
        return;
      }
      if (params['openAddModal'] === 'true' || params['openAddModal'] === true) {
        const leadId = Number(params['leadId']);
        const appointmentId = Number(params['appointmentId']);
        this.linkAppointmentId = Number.isFinite(appointmentId) && appointmentId > 0 ? appointmentId : undefined;

        if (Number.isFinite(leadId) && leadId > 0) {
          // Open only once the seed is in hand: PatientModal applies it on init,
          // so arriving late would leave the form blank.
          this.leadConversion.getLeadContact(leadId).subscribe({
            next: (contact) => {
              this.patientSeed = contact;
              this.openModal();
            },
            // A lead we cannot read (wrong clinic, or deleted) still opens an
            // empty form rather than a dead page.
            error: () => this.openModal(),
          });
        } else {
          this.openModal();
        }
      }
    });
  }

  ngAfterViewInit() {
    // Assign templates after they are initialized in the view
    setTimeout(() => {
      this.templates = {
        photo: this.photoTemplate,
        status: this.statusTemplate,
        primary_phone: this.cellTemplate,
        created_by: this.createdByTemplate,
        updated_by: this.updatedByTemplate,
        actions: this.actionsTemplate
      };
    });
  }



  onSearch(term: string) {
    this.search$.next(term);
  }

  onPageChange(page: number) {
    this.page$.next(page);
  }

  onSort(event: any) {
    // For now, sorting is handled client-side by PrimeNG on the current page
    // If you want server-side sorting, you would:
    // 1. Store the sort field and order
    // 2. Pass them to the backend API
    // 3. Trigger a refresh
    console.log('Sort event:', event);
  }

  toggleStatus(row: any) {
    const newStatus = row.status === 'ativo' ? 'inativo' : 'ativo';
    this.patientsService.updatePatient(row.id, { status: newStatus }).subscribe(() => {
      this.isManualRefresh = true;
      this.refresh$.next();
    });
  }

  deletePatientGdpr(row: any) {
    this.confirmationService.confirm({
      message: this.translate.instant('patients_gdpr_confirm_message', { name: row.name }),
      header: this.translate.instant('patients_gdpr_confirm_title'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      accept: () => {
        this.patientsService.gdprAnonymizePatient(row.id).subscribe({
          next: () => {
            toast.success(this.translate.instant('patients_gdpr_success'));
            this.isManualRefresh = true;
            this.refresh$.next();
          },
          error: (err) => {
            console.error('Error anonymizing patient:', err);
            toast.error(this.translate.instant('patients_gdpr_error'));
          },
        });
      },
    });
  }

  openModal(patientId?: number) {
    this.selectedPatientId = patientId;
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.selectedPatientId = undefined;
    this.patientSeed = null;
    this.linkAppointmentId = undefined;
  }

  onPatientSaved() {
    this.closeModal();
    this.isManualRefresh = true;
    this.refresh$.next();
  }
}
